import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { GraduationCap, CircleNotch, WarningCircle, Leaf } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function HandoffPage() {
  const { handoffLogin } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("loading"); // loading | error | optin
  const [returnPath, setReturnPath] = useState("/dashboard");
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // run once (guards React StrictMode double-invoke)
    ran.current = true;
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) { setState("error"); setError("Missing handoff token."); return; }
    (async () => {
      const data = await handoffLogin(token);
      if (data.status === "opt_in_required") {
        setReturnPath(data.return_path || "/dashboard");
        setState("optin");
        return;
      }
      // navigate(replace) clears the ?token from the URL and keeps the router in sync.
      if (data.manage_granted === false) {
        toast.error("This institution is already managed by another account — request access from its manager.");
      } else if (data.manage_granted === true) {
        toast.success("You can now manage this institution's website.");
      }
      nav(data.return_path || "/dashboard", { replace: true });
    })().catch((e) => { setState("error"); setError(e?.response?.data?.detail || "This sign-in link is invalid or has expired."); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="grid min-h-screen place-items-center bg-background px-6" data-testid="handoff-page">
      <div className="w-full max-w-sm text-center">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-primary text-primary-foreground"><GraduationCap weight="fill" size={24} /></span>
        {state === "loading" && (<>
          <CircleNotch size={26} className="mx-auto mt-6 animate-spin text-secondary" />
          <p className="mt-4 text-sm text-muted-foreground" data-testid="handoff-status">Signing you in…</p>
        </>)}
        {state === "optin" && (<>
          <Leaf size={26} className="mx-auto mt-6 text-secondary" />
          <h1 className="mt-3 font-heading text-xl font-medium">One quick opt-in</h1>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="handoff-optin">To move between NaturePulse and AlumniPulse, turn on "Environmental &amp; Research sharing" in your profile.</p>
          <Button className="mt-6 rounded-full" onClick={() => nav("/profile/edit", { replace: true })} data-testid="handoff-optin-btn">Open profile settings</Button>
        </>)}
        {state === "error" && (<>
          <WarningCircle size={26} className="mx-auto mt-6 text-destructive" />
          <p className="mt-4 text-sm text-destructive" data-testid="handoff-error">{error}</p>
          <Button variant="outline" className="mt-6 rounded-full" onClick={() => nav("/login", { replace: true })} data-testid="handoff-back-login">Back to sign in</Button>
        </>)}
      </div>
    </div>
  );
}
